import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useState } from 'react';

export const MENU_BAR_HEIGHT = 28;
export const DOCK_HEIGHT = 76;

export const WALLPAPERS = [
	{ id: 'aurora', label: 'Aurora', css: 'linear-gradient(160deg, #1b2340 0%, #2a1b4a 45%, #0b0d14 100%)' },
	{ id: 'sunset', label: 'Sunset', css: 'linear-gradient(160deg, #3a1c5c 0%, #a3416b 55%, #ff8a5b 100%)' },
	{ id: 'graphite', label: 'Graphite', css: 'linear-gradient(160deg, #3a3a3c 0%, #1c1c1e 100%)' },
	{ id: 'ocean', label: 'Ocean', css: 'linear-gradient(160deg, #0b2545 0%, #134074 45%, #13315c 100%)' },
];

const WALLPAPER_KEY = 'os-wallpaper';

const initialState = { windows: {}, order: [], activeId: null };

function focusReducer(state, appId) {
	const order = [...state.order.filter(id => id !== appId), appId];
	return { ...state, order, activeId: appId };
}

function reducer(state, action) {
	switch (action.type) {
		case 'OPEN': {
			const { appId, defaultSize } = action;
			const existing = state.windows[appId];
			if (existing) {
				if (existing.minimized) {
					return focusReducer(
						{ ...state, windows: { ...state.windows, [appId]: { ...existing, minimized: false } } },
						appId
					);
				}
				return focusReducer(state, appId);
			}
			const count = state.order.length;
			const offset = (count % 6) * 28;
			const width = defaultSize?.width || 560;
			const height = defaultSize?.height || 420;
			const win = {
				appId,
				x: 120 + offset,
				y: 90 + offset,
				width,
				height,
				minimized: false,
				maximized: false,
				prevBounds: null,
			};
			return focusReducer({ ...state, windows: { ...state.windows, [appId]: win } }, appId);
		}
		case 'CLOSE': {
			if (!state.windows[action.appId]) return state;
			const windows = { ...state.windows };
			delete windows[action.appId];
			const order = state.order.filter(id => id !== action.appId);
			const activeId = state.activeId === action.appId ? order[order.length - 1] || null : state.activeId;
			return { ...state, windows, order, activeId };
		}
		case 'FOCUS': {
			if (!state.windows[action.appId]) return state;
			return focusReducer(state, action.appId);
		}
		case 'MINIMIZE': {
			const win = state.windows[action.appId];
			if (!win) return state;
			// Keep appId in `order` (unlike CLOSE) so the Window stays mounted and can
			// play its shrink-to-dock animation instead of unmounting instantly.
			const windows = { ...state.windows, [action.appId]: { ...win, minimized: true } };
			let activeId = state.activeId;
			if (activeId === action.appId) {
				activeId = null;
				for (let i = state.order.length - 1; i >= 0; i--) {
					const id = state.order[i];
					if (id !== action.appId && windows[id] && !windows[id].minimized) {
						activeId = id;
						break;
					}
				}
			}
			return { ...state, windows, activeId };
		}
		case 'TOGGLE_MAXIMIZE': {
			const win = state.windows[action.appId];
			if (!win) return state;
			if (win.maximized) {
				return {
					...state,
					windows: {
						...state.windows,
						[action.appId]: { ...win, maximized: false, ...(win.prevBounds || {}), prevBounds: null },
					},
				};
			}
			return {
				...state,
				windows: {
					...state.windows,
					[action.appId]: {
						...win,
						maximized: true,
						prevBounds: { x: win.x, y: win.y, width: win.width, height: win.height },
						...action.bounds,
					},
				},
			};
		}
		case 'MOVE': {
			const win = state.windows[action.appId];
			if (!win || win.maximized) return state;
			return { ...state, windows: { ...state.windows, [action.appId]: { ...win, x: action.x, y: action.y } } };
		}
		case 'RESIZE': {
			const win = state.windows[action.appId];
			if (!win || win.maximized) return state;
			return { ...state, windows: { ...state.windows, [action.appId]: { ...win, ...action.bounds } } };
		}
		case 'SYNC_MAXIMIZED_BOUNDS': {
			const win = state.windows[action.appId];
			if (!win || !win.maximized) return state;
			return { ...state, windows: { ...state.windows, [action.appId]: { ...win, ...action.bounds } } };
		}
		default:
			return state;
	}
}

const OSContext = createContext(null);

/**
 * OSProvider wires up window-manager state plus OS-mode-only cosmetic state
 * (wallpaper) and re-exposes the shared site theme (theme/setTheme come from
 * App.jsx so the "Exit OS Mode" control in SettingsApp can switch themes).
 */
export function OSProvider({ children, theme, onThemeChange }) {
	const [state, dispatch] = useReducer(reducer, initialState);

	const [wallpaper, setWallpaperState] = useState(() => {
		if (typeof window === 'undefined') return WALLPAPERS[0].id;
		return window.localStorage.getItem(WALLPAPER_KEY) || WALLPAPERS[0].id;
	});

	const [isMobile, setIsMobile] = useState(() =>
		typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
	);

	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const mql = window.matchMedia('(max-width: 767px)');
		const handler = e => setIsMobile(e.matches);
		mql.addEventListener('change', handler);
		return () => mql.removeEventListener('change', handler);
	}, []);

	const setWallpaper = useCallback(id => {
		setWallpaperState(id);
		if (typeof window !== 'undefined') window.localStorage.setItem(WALLPAPER_KEY, id);
	}, []);

	// NOTE: openApp is the one exception to the "takes an appId string" rule —
	// it takes the FULL app registry object (see apps.js) so it can seed the
	// window with that app's defaultSize without OSContext importing apps.js
	// (which would create a circular import: OSContext -> apps.js -> *App.jsx -> OSContext).
	const openApp = useCallback(app => {
		dispatch({ type: 'OPEN', appId: app.id, defaultSize: app.defaultSize });
	}, []);
	const closeWindow = useCallback(appId => dispatch({ type: 'CLOSE', appId }), []);
	const focusWindow = useCallback(appId => dispatch({ type: 'FOCUS', appId }), []);
	const minimizeWindow = useCallback(appId => dispatch({ type: 'MINIMIZE', appId }), []);
	const toggleMaximize = useCallback(appId => {
		const bounds = {
			x: 0,
			y: MENU_BAR_HEIGHT,
			width: window.innerWidth,
			height: window.innerHeight - MENU_BAR_HEIGHT,
		};
		dispatch({ type: 'TOGGLE_MAXIMIZE', appId, bounds });
	}, []);
	const moveWindow = useCallback((appId, x, y) => dispatch({ type: 'MOVE', appId, x, y }), []);
	const resizeWindow = useCallback((appId, bounds) => dispatch({ type: 'RESIZE', appId, bounds }), []);

	// Keep maximized windows filling the viewport if the browser is resized.
	useEffect(() => {
		const handleResize = () => {
			Object.values(state.windows).forEach(win => {
				if (win.maximized) {
					dispatch({
						type: 'SYNC_MAXIMIZED_BOUNDS',
						appId: win.appId,
						bounds: { x: 0, y: MENU_BAR_HEIGHT, width: window.innerWidth, height: window.innerHeight - MENU_BAR_HEIGHT },
					});
				}
			});
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [state.windows]);

	const value = useMemo(
		() => ({
			windows: state.windows,
			order: state.order,
			activeId: state.activeId,
			openApp,
			closeWindow,
			focusWindow,
			minimizeWindow,
			toggleMaximize,
			moveWindow,
			resizeWindow,
			theme,
			setTheme: onThemeChange,
			wallpaper,
			setWallpaper,
			isMobile,
		}),
		[
			state,
			openApp,
			closeWindow,
			focusWindow,
			minimizeWindow,
			toggleMaximize,
			moveWindow,
			resizeWindow,
			theme,
			onThemeChange,
			wallpaper,
			setWallpaper,
			isMobile,
		]
	);

	return <OSContext.Provider value={value}>{children}</OSContext.Provider>;
}

/**
 * useOS() contract:
 *   windows        { [appId]: { appId, x, y, width, height, minimized, maximized, prevBounds } }
 *   order          [appId] back-to-front, last = topmost
 *   activeId       appId of focused window, or null
 *   openApp(app)   app = FULL registry object from apps.js (not just the id)
 *   closeWindow(appId) / focusWindow(appId) / minimizeWindow(appId) / toggleMaximize(appId)
 *   moveWindow(appId, x, y)
 *   resizeWindow(appId, { x?, y?, width?, height? })
 *   theme          current site theme ('os')
 *   setTheme(name) switch site theme, e.g. setTheme('legacy') to leave OS mode
 *   wallpaper / setWallpaper(id)   OS-mode-only, id from WALLPAPERS
 *   isMobile       boolean, viewport < 768px
 */
export function useOS() {
	const ctx = useContext(OSContext);
	if (!ctx) throw new Error('useOS must be used within an OSProvider');
	return ctx;
}
